/**
 * Dragging a space to a new position (RFC 0281 §7).
 *
 * ONE COPY OF THE BEHAVIOUR, TWO PLACES IT IS DRAWN, for the reason
 * `useSpaceActions` gives at length: spaces render as sidebar rows and as tabs
 * along the top, and an interaction implemented twice drifts the moment either
 * copy grows. What differs between a row and a tab is the AXIS, so that is the
 * only thing this takes as an argument.
 *
 * REORDERING IS NOT COSMETIC. `SpacesDoc.order` is both the order the strip is
 * drawn in and what `ctrl+1..9` mean, so a drag rebinds a keyboard shortcut. It
 * syncs, so it rebinds it on every device.
 *
 * THE INDICATOR IS A GAP, NOT A ROW. Highlighting the row you are over says
 * which row, not which side of it, and those are different drops. `dropIndex`
 * is an INSERTION point between rows, which is the thing the person is actually
 * choosing.
 */

import { useCallback, useState } from 'react'

/**
 * Distinct from the sidebar's item MIME on purpose.
 *
 * That one files a session or a document into a group. A space is neither, and
 * sharing a type would let a space be dropped into a group, and a session onto
 * the space strip, with both reading as a no-op the person has to explain to
 * themselves.
 */
export const SPACE_DRAG_MIME = 'application/x-mod-space'

export interface SpaceReorder {
  /** The space under the cursor mid-drag, or null when nothing is dragging. */
  draggingId: string | null
  /**
   * Where a drop would INSERT, counted in gaps: 0 is before the first space,
   * `spaces.length` is after the last. Null when no drop is being offered.
   */
  dropIndex: number | null
  /** Whether the gap before space `index` is the one being offered. */
  isDropGap: (index: number) => boolean
  /** Props for a draggable space, at its position in the list. */
  spaceProps: (
    spaceId: string,
    index: number,
  ) => {
    draggable: boolean
    onDragStart: (e: React.DragEvent) => void
    onDragEnd: () => void
    onDragOver: (e: React.DragEvent) => void
    onDrop: (e: React.DragEvent) => void
  }
  /**
   * Props for the strip itself, so a drop in the empty space past the last
   * item still lands rather than being swallowed as "not over anything".
   */
  stripProps: {
    onDragOver: (e: React.DragEvent) => void
    onDrop: (e: React.DragEvent) => void
    onDragLeave: () => void
  }
}

export function useSpaceReorder({
  spaces,
  onReorder,
  axis,
}: {
  spaces: readonly { spaceId: string }[]
  /** Absent means this rendering is read-only, and nothing becomes draggable. */
  onReorder?: (spaceId: string, toIndex: number) => void
  /** Rows run down the sidebar, tabs run across the top. */
  axis: 'x' | 'y'
}): SpaceReorder {
  const [draggingId, setDraggingId] = useState<string | null>(null)
  const [dropIndex, setDropIndex] = useState<number | null>(null)

  const clear = useCallback(() => {
    setDraggingId(null)
    setDropIndex(null)
  }, [])

  const commit = useCallback(
    (spaceId: string, at: number) => {
      const from = spaces.findIndex((s) => s.spaceId === spaceId)
      if (from === -1) return
      // `reorderSpace` splices the space OUT before putting it back, so its
      // index counts positions in the shortened list. A gap after the space's
      // own position is therefore one lower than the gap the person saw.
      const to = from < at ? at - 1 : at
      if (to === from) return
      onReorder?.(spaceId, to)
    },
    [spaces, onReorder],
  )

  const spaceProps = useCallback(
    (spaceId: string, index: number) => ({
      draggable: Boolean(onReorder),
      onDragStart: (e: React.DragEvent) => {
        e.dataTransfer.setData(SPACE_DRAG_MIME, spaceId)
        e.dataTransfer.effectAllowed = 'move'
        setDraggingId(spaceId)
      },
      onDragEnd: clear,
      onDragOver: (e: React.DragEvent) => {
        if (!draggingId) return
        // preventDefault is what makes an element a drop target at all. Without
        // it the browser refuses the drop and the drag animates back, which
        // reads as the feature being broken rather than as a refusal.
        e.preventDefault()
        e.dataTransfer.dropEffect = 'move'
        const r = e.currentTarget.getBoundingClientRect()
        const past =
          axis === 'x'
            ? e.clientX > r.left + r.width / 2
            : e.clientY > r.top + r.height / 2
        setDropIndex(index + (past ? 1 : 0))
      },
      onDrop: (e: React.DragEvent) => {
        e.preventDefault()
        e.stopPropagation()
        const id = e.dataTransfer.getData(SPACE_DRAG_MIME) || draggingId
        if (id && dropIndex !== null) commit(id, dropIndex)
        clear()
      },
    }),
    [axis, clear, commit, draggingId, dropIndex, onReorder],
  )

  const stripProps = {
    onDragOver: (e: React.DragEvent) => {
      if (!draggingId) return
      e.preventDefault()
      e.dataTransfer.dropEffect = 'move'
      // Only claim the tail gap. A space's own handler has already set a
      // precise index and this fires after it, so overwriting unconditionally
      // would snap every drop to the end.
      if (dropIndex === null) setDropIndex(spaces.length)
    },
    onDrop: (e: React.DragEvent) => {
      e.preventDefault()
      const id = e.dataTransfer.getData(SPACE_DRAG_MIME) || draggingId
      if (id && dropIndex !== null) commit(id, dropIndex)
      clear()
    },
    onDragLeave: () => setDropIndex(null),
  }

  return {
    draggingId,
    dropIndex,
    isDropGap: (index: number) => draggingId !== null && dropIndex === index,
    spaceProps,
    stripProps,
  }
}
